import { TestCaseProp, CustomStepProp } from '@Models/types';
import { PencilSimple } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';

interface Props {
  testcase: TestCaseProp;
  setSelectedTestCase: (testCase: TestCaseProp) => void;
  isEditable: boolean;
}

const TestCase: React.FC<Props> = ({
  testcase,
  setSelectedTestCase,
  isEditable,
}) => {
  const [isEditingPreconds, setIsEditingPreconds] = useState(false);
  const [isEditingSteps, setIsEditingSteps] = useState(false);
  const [preconds, setPreconds] = useState<string[]>([]);
  const [steps, setSteps] = useState<CustomStepProp[]>([]);
  const [initialPreconds, setInitialPreconds] = useState<string[]>([]);
  const [initialSteps, setInitialSteps] = useState<CustomStepProp[]>([]);

  const updateTestCase = (
    field: keyof TestCaseProp,
    value: string | object
  ) => {
    const updatedTestCases: TestCaseProp = { ...testcase, [field]: value };
    console.log('updateTestCase', field, value, updatedTestCases);
    setSelectedTestCase(updatedTestCases);
  };

  const updatePreconds = () => {
    const precondsString = preconds.join(' - ');
    updateTestCase('custom_preconds', precondsString);
    setIsEditingPreconds(false);
  };

  const updateSteps = () => {
    updateTestCase('custom_steps_separated', steps);
    setIsEditingSteps(false);
  };

  const cancelPreconds = () => {
    setPreconds(initialPreconds);
    setIsEditingPreconds(false);
  };

  const cancelSteps = () => {
    setSteps(initialSteps);
    setIsEditingSteps(false);
  };

  useEffect(() => {
    const initialPreconds = testcase.custom_preconds
      .split('-')
      .map((precond) => precond.trim())
      .filter((precond) => precond !== '');
    setPreconds(initialPreconds);
    setInitialPreconds(initialPreconds);
    setIsEditingPreconds(false);
  }, [testcase.custom_preconds]);

  useEffect(() => {
    const initialSteps = testcase.custom_steps_separated.map((step) => ({
      content: step.content.trim(),
      expected: step.expected.trim(),
    }));
    setSteps(initialSteps);
    setInitialSteps(initialSteps);
    setIsEditingSteps(false);
  }, [testcase.custom_steps_separated]);

  const splitExpectedText = (expectedText: string): string[] => {
    let text = expectedText.trim();
    if (text.startsWith('-')) {
      text = text.replace(/^-+\s*/, '');
    }
    return text.split(/\s+-\s+/).filter((part) => part !== '');
  };

  return (
    <>
      <div className="mt-1 flex flex-row items-center text-xl font-bold">
        <label>{testcase.title}</label>
      </div>
      {/* Pre-Conditions */}
      <div className="custom-scroll mt-4 flex flex-grow flex-col gap-y-7 overflow-y-auto">
        <div className="flex w-full flex-row items-center">
          <label className="w-1/4 min-w-24">Pre-Conditions:</label>
          {!isEditingPreconds ? (
            <div className="mr-3 w-full rounded-md bg-gray-200 px-4 py-2">
              {preconds.map((precond, index) => (
                <div
                  key={index}
                  className={`${index !== 0 ? 'border-t' : ''} mb-2 border-gray-300`}
                >
                  {precond}
                </div>
              ))}
            </div>
          ) : (
            <div className="w-full">
              {preconds.map((precond, index) => (
                <input
                  key={index}
                  type="text"
                  className="mb-2 w-full rounded-md bg-gray-200 px-4 py-2"
                  value={precond}
                  onChange={(e) => {
                    const updatedPreconds = [...preconds];
                    updatedPreconds[index] = e.target.value;
                    setPreconds(updatedPreconds);
                  }}
                />
              ))}
            </div>
          )}
          <div className="ml-2 flex flex-col">
            {isEditable && isEditingPreconds && (
              <button
                onClick={updatePreconds}
                className="m-1 flex items-center space-x-2 whitespace-nowrap rounded-md bg-primary-700 px-2 py-2 text-xs text-white"
              >
                <PencilSimple size={12} />
                <span>Save</span>
              </button>
            )}
            {isEditable && (
              <button
                onClick={() => {
                  if (isEditingPreconds) {
                    cancelPreconds();
                  } else {
                    setIsEditingPreconds(true);
                  }
                }}
                className="m-1 flex items-center space-x-2 whitespace-nowrap rounded-md bg-primary-700 px-2 py-2 text-xs text-white"
              >
                <PencilSimple size={12} />
                <span>{isEditingPreconds ? 'Cancel' : 'Edit'}</span>
              </button>
            )}
          </div>
        </div>
        {/* Steps */}
        <div className="flex w-full flex-row items-center">
          <label className="w-1/4 min-w-24">Steps:</label>
          {!isEditingSteps ? (
            <div className="custom-scroll mr-3 h-40 w-full overflow-y-auto rounded-md bg-gray-200 p-3 px-4 py-2">
              {steps.map((step, index) => (
                <div
                  key={index}
                  className={`${index !== 0 ? 'border-t' : ''} mb-2 border-gray-300`}
                >
                  <div>
                    <span className="font-medium">Content:</span> {step.content}
                  </div>
                  <div>
                    <span className="font-medium">Expected:</span>
                    {splitExpectedText(step.expected).length > 1 ? (
                      <div className="mt-1">
                        {splitExpectedText(step.expected).map((part, i) => (
                          <div key={i}>{part}</div>
                        ))}
                      </div>
                    ) : (
                      <span> {step.expected}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="custom-scroll max-h-40 w-full overflow-y-auto">
              {steps.map((step, index) => (
                <div key={index} className="mb-2 w-full">
                  <div className="mb-1">
                    <label className="block font-medium">Content:</label>
                    <textarea
                      className="w-full rounded-md bg-gray-200 px-4 py-2"
                      value={step.content}
                      onChange={(e) => {
                        const updatedSteps = [...steps];
                        updatedSteps[index] = {
                          ...updatedSteps[index],
                          content: e.target.value,
                        };
                        setSteps(updatedSteps);
                      }}
                    />
                  </div>
                  <div>
                    <label className="block font-medium">Expected:</label>
                    <textarea
                      className="w-full rounded-md bg-gray-200 px-4 py-2"
                      value={step.expected}
                      onChange={(e) => {
                        const updatedSteps = [...steps];
                        updatedSteps[index] = {
                          ...updatedSteps[index],
                          expected: e.target.value,
                        };
                        setSteps(updatedSteps);
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="ml-2 flex flex-col">
            {isEditable && isEditingSteps && (
              <button
                onClick={updateSteps}
                className="m-1 flex items-center space-x-2 whitespace-nowrap rounded-md bg-primary-700 px-2 py-2 text-xs text-white"
              >
                <PencilSimple size={12} />
                <span>Save</span>
              </button>
            )}
            {isEditable && (
              <button
                onClick={() => {
                  if (isEditingSteps) {
                    cancelSteps();
                  } else {
                    setIsEditingSteps(true);
                  }
                }}
                className="m-1 flex items-center space-x-2 whitespace-nowrap rounded-md bg-primary-700 px-2 py-2 text-xs text-white"
              >
                <PencilSimple size={12} />
                <span>{isEditingSteps ? 'Cancel' : 'Edit'}</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default TestCase;
